import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import '../../styles/transitions.css';
import LeftPanel from "../../features/tickets/left-panel/LeftPanel";
import RightPanel from "../../features/tickets/right-panel/RightPanel";
import { PanelProvider } from "../providers/PanelContext";
import { TicketContext } from "../providers/TicketContext";
import { PanelContext } from "../providers/PanelContext";
import { FlowChartProvider } from "../providers/FlowChartContext";
import DebugPanel from "../../shared/ui/DebugPanel";

const MainPageContent = () => {
  const { selectedTicket } = useContext(TicketContext);
  const { selectedPanel, isLeftPanelCollapsed } = useContext(PanelContext);
  const [leftPanelWidth, setLeftPanelWidth] = useState(30);
  const savedWidthRef = useRef(30);
  const isResizingRef = useRef(false);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const rafRef = useRef(null);

  const rightPanelWidth = (100 - leftPanelWidth).toFixed(2);

  useEffect(() => {
    if (selectedPanel === 'home') {
      setLeftPanelWidth(100);
    } else if (!selectedTicket) {
      setLeftPanelWidth(30);
    } else if (selectedTicket && selectedPanel === 'flow') {
      setLeftPanelWidth(prev => prev > 50 ? 50 : prev);
    }
  }, [selectedPanel, selectedTicket]);

  useEffect(() => {
    if (isLeftPanelCollapsed) {
      savedWidthRef.current = leftPanelWidth;
      setLeftPanelWidth(0);
    } else if (savedWidthRef.current > 0) {
      setLeftPanelWidth(savedWidthRef.current);
    }
  }, [isLeftPanelCollapsed]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingRef.current) return;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const newWidth = (e.clientX / window.innerWidth) * 100;
        if (newWidth >= 20 && newWidth <= 70) {
          if (leftPanelRef.current) leftPanelRef.current.style.width = `${newWidth}%`;
          if (rightPanelRef.current) rightPanelRef.current.style.width = `${(100 - newWidth).toFixed(2)}%`;
          setLeftPanelWidth(newWidth);
        }
      });
    };

    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="flex h-screen bg-background overflow-hidden relative">
      <div
        ref={leftPanelRef}
        className="bg-primary-dark text-text border-r border-blue-300 h-full"
        style={{
          width: isLeftPanelCollapsed ? '0%' : `${leftPanelWidth}%`,
          minWidth: isLeftPanelCollapsed ? 0 : undefined,
          overflow: isLeftPanelCollapsed ? 'hidden' : 'auto',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <LeftPanel />
      </div>

      <div
        onMouseDown={handleMouseDown}
        className={`w-1 bg-inactive cursor-col-resize hover:bg-inactive-dark active:bg-blue-500 z-10 flex-shrink-0 ${
          selectedPanel === 'home' || isLeftPanelCollapsed ? 'hidden' : ''
        }`}
      />

      {selectedPanel !== 'home' && (
        <div
          ref={rightPanelRef}
          className="bg-white text-text overflow-auto h-full"
          style={{
            width: isLeftPanelCollapsed ? '100%' : `${rightPanelWidth}%`,
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <RightPanel />
        </div>
      )}
      
      <DebugPanel />
    </div>
  );
};

const MainPage = () => {
  return (
    <PanelProvider>
      <FlowChartProvider>
        <MainPageContent />
      </FlowChartProvider>
    </PanelProvider>
  );
};

export default MainPage;